import { GridConfig } from "../GridConfig";
import { ConnectingLineHandler } from "../ConnectingLineHandler";
import { GRID_DIRECTION } from "../../const/grid-directions.const";
import { LineClipping } from "../../utils/LineClipping";

/**
 * @Description Handles the setting of obstacles during pathfinding between elements
 */
export class ObstacleStrategies {
    gridConfig;

    connectingLineHandler;

    BREAK_FACTOR = 10;

    grid;

    STRATEGIES = {
        BLOCKED_CONNECTIONS: "BLOCKED_CONNECTIONS",
        INTERMITTENT_BLOCKED_CONNECTIONS: "INTERMITTENT_BLOCKED_CONNECTIONS",
        FREE_CONNECTIONS: "FREE_CONNECTIONS"
    };

    constructor(grid, instanceId) {
        this.grid = grid;
        this.instanceId = instanceId;
        this.gridConfig = new GridConfig(this.instanceId);
        this.connectingLineHandler = new ConnectingLineHandler(this.instanceId);
    }

    /**
     * @description - returns all available strategies
     */
    getStrategies() {
        return Object.keys(this.STRATEGIES);
    }

    /**
     * @description Sets the obstacles in a provided matrix to be used in pathfinding algorithm
     * @param {[][]} matrix
     * @param {*} startPoint
     * @param {*} endPoints
     * @param {*} strategy
     * @param {*} linesToIgnore
     */
    setObstacles(matrix, startPoint, endPoints, strategy, linesToIgnore) {
        const withLinesStrat = (strategy === this.STRATEGIES.INTERMITTENT_BLOCKED_CONNECTIONS || strategy === this.STRATEGIES.BLOCKED_CONNECTIONS);
        for (let row = 0; row < this.grid.length; row++) {
            for (let col = 0; col < this.grid[row].length; col++) {
                const cell = this.grid[row][col];
                if (!cell.hasContent() && withLinesStrat && this.connectingLineHandler.linePassesThroughCell(cell.uuid)) {
                    this.blockLineCells(cell, matrix, strategy, linesToIgnore);
                }
                if (!cell.hasContent()) continue;
                this.setObstaclesInExpandedCell(row, col, matrix, startPoint, endPoints);
            }
        }
        return matrix;
    }

    blockLineCells(cell, matrix, strat, linesToIgnore) {
        const connectorsToClip = [];
        const lineClipper = new LineClipping();
        // TODO: Performance Improvement. Keep a reference on each passingThrough cell on the connectingLineHandler object to the
        //      connectors IDS that pass through the cell
        this.connectingLineHandler.connectors.forEach(connector => {
            if (connector.passesThrough.indexOf(cell.uuid) !== -1 && linesToIgnore.indexOf(connector.instance.uuid) === -1) {
                connectorsToClip.push(connector);
            }
        });
        for (let i = 0; i < connectorsToClip.length; i++) {
            const cellLayout = cell.layout;
            const rectangular = [cellLayout.x, cellLayout.y, cellLayout.x + this.gridConfig.cellWidth, cellLayout.y + this.gridConfig.cellHeight];
            const { path } = connectorsToClip[i].instance.layout;
            const matrixPath = this.connectingLineHandler.utils.turnObjectPathIntoMatrixPath(path);
            const intersection = lineClipper.lineclip(matrixPath, rectangular);
            // let expandedPath = PF.Util.expandPath(intersection);
            const objectIntersections = this.connectingLineHandler.utils.turnMatrixIntoObjectPath(intersection);
            for (let interI = 0; interI < objectIntersections.length; interI++) {
                const inter = objectIntersections[interI];
                for (let x = 0; x < inter.length; x++) {
                    const pointA = inter[x];
                    const pointB = inter[x + 1];
                    if (pointA && pointB) {
                        this.recursivelyBlockBreakFactorLinePortion(pointA, pointB, matrix, null, strat);
                    }
                }
            }
        }
    }

    recursivelyBlockBreakFactorLinePortion(a, b, matrix, stopAt, strat, lastMarked) {
        if (!stopAt) stopAt = b;
        const markIntermitent = strat === this.STRATEGIES.INTERMITTENT_BLOCKED_CONNECTIONS;
        const minifiedCellPos = {
            x: (a.x - this.gridConfig.viewPadding) / this.BREAK_FACTOR,
            y: (a.y - this.gridConfig.viewPadding) / this.BREAK_FACTOR
        };
        if ((markIntermitent && !lastMarked) || (!markIntermitent)) {
            matrix.setWalkableAt(minifiedCellPos.x, minifiedCellPos.y, false);
        }
        if (stopAt.x !== a.x || stopAt.y !== a.y) {
            const val = {};
            if (stopAt.x !== a.x) {
                val.x = a.x + (stopAt.x > a.x ? this.BREAK_FACTOR : (this.BREAK_FACTOR - (this.BREAK_FACTOR * 2)));
            }
            if (stopAt.y !== a.y) {
                val.y = a.y + (stopAt.y > a.y ? this.BREAK_FACTOR : (this.BREAK_FACTOR - (this.BREAK_FACTOR * 2)));
            }
            if (!val.x) val.x = stopAt.x;
            if (!val.y) val.y = stopAt.y;
            this.recursivelyBlockBreakFactorLinePortion(val, b, matrix, stopAt, strat, !lastMarked);
        }
    }

    /**
     * @description sets obstancles on the provided cell
     * @param {Number} row
     * @param {Number} col
     * @param {Matrix} matrix
     * @param {*} startPoints
     * @param {*} endPoints
     */
    setObstaclesInExpandedCell(row, col, matrix, startPoints, endPoints) {
        const cellExpandedRowsStart = row * this.BREAK_FACTOR + 1;
        const cellExpandedRowsEnd = cellExpandedRowsStart + this.BREAK_FACTOR - 1;

        const cellExpandedColsStart = col * this.BREAK_FACTOR + 1;
        const cellExpandedColsEnd = cellExpandedColsStart + this.BREAK_FACTOR - 1;

        for (let cellRow = cellExpandedRowsStart; cellRow < cellExpandedRowsEnd; cellRow++) {
            const rowIteration = cellRow;
            for (let cellCell = cellExpandedColsStart; cellCell < cellExpandedColsEnd; cellCell++) {
                const cellIteration = cellCell;
                const isAStartPoint = startPoints.filter((point) => point.row === rowIteration && point.col === cellIteration).length > 0;
                const isAEndPoint = endPoints.filter((point) => point.row === rowIteration && point.col === cellIteration).length > 0;
                const mainCellLimits = {
                    colStart: cellExpandedColsStart,
                    colEnd: cellExpandedColsEnd,
                    rowStart: cellExpandedRowsStart,
                    rowEnd: cellExpandedRowsEnd
                };
                if (!isAStartPoint && !isAEndPoint && !this.checkIfMinifiedCellIsExit(cellIteration, rowIteration, startPoints, endPoints, mainCellLimits)) {
                    if (cellIteration >= matrix.width || rowIteration >= matrix.height) {
                        console.error("Tried setting walkable at a invalid cell position");
                        continue;
                    }
                    matrix.setWalkableAt(cellIteration, rowIteration, false);
                }
            }
        }
    }

    /**
     * @description Checks if the provided cell is a connector exit
     */
    checkIfMinifiedCellIsExit(cell, row, starts, ends, workingCell) {
        const arr = [...starts, ...ends];
        for (let i = 0; i < arr.length; i++) {
            const endpoint = arr[i];
            const insideEndpointCell = endpoint.col >= workingCell.colStart && endpoint.col <= workingCell.colEnd
                && endpoint.row >= workingCell.rowStart && endpoint.row <= workingCell.rowEnd;
            if (!insideEndpointCell) continue;
            if (endpoint.side === GRID_DIRECTION.LEFT) {
                if (row === endpoint.row && endpoint.col > cell && cell >= workingCell.colStart) return true;
                continue;
            }
            if (endpoint.side === GRID_DIRECTION.RIGHT) {
                if (row === endpoint.row && endpoint.col < cell && cell <= workingCell.colEnd) return true;
                continue;
            }
            if (endpoint.side === GRID_DIRECTION.TOP) {
                if (cell === endpoint.col && endpoint.row > row && row >= workingCell.rowStart) return true;
                continue;
            }
            if (endpoint.side === GRID_DIRECTION.BOTTOM) {
                if (cell === endpoint.col && endpoint.row < row && row <= workingCell.rowEnd) return true;
                continue;
            }
        }

        return false;
    }
}
